import { PlaceholderKey } from '../placeholder/placeholder-keys';
import { IndicatorSubtype } from './indicator-subtype';
import { TemplateType } from './template-type';

/**
 * Per-type metadata for media-manager templates. Open shape: the optional
 * `subtypes` block is present only on types that have a sub-dimension. Other
 * types may grow their own subtype block (possibly with different option lists
 * or `multi: true`) without forcing every entry to declare an empty one.
 */
export interface TemplateTypeSubtypeConfig {
  /** Label shown next to the subtype selector in the UI. */
  label: string;
  /** Option list rendered in the selector. */
  options: readonly { value: string; label: string }[];
  /** When true, the UI must allow multiple subtypes to be picked at once. */
  multi: boolean;
  /** When true, the user must pick at least one subtype before submit. */
  required: boolean;
}

export interface TemplateTypeEntry {
  /** Display name in the create/edit modals and the editor header. */
  label: string;
  /** Placeholder keys that may appear on a template of this type. */
  allowedPlaceholderKeys: ReadonlySet<PlaceholderKey>;
  /** Present only when the type carries a sub-dimension. */
  subtypes?: TemplateTypeSubtypeConfig;
}

// Why: a placeholder catalog this small reads better as one literal than as
// many tiny constants. The `set(...)` helper keeps each entry to one visual line.
const set = (...keys: PlaceholderKey[]): ReadonlySet<PlaceholderKey> => new Set(keys);

const SCHEDULE_BASE_KEYS: readonly PlaceholderKey[] = [
  PlaceholderKey.TEAM_NAME,
  PlaceholderKey.TEAM_SHORT_NAME,
  PlaceholderKey.TEAM_LOGO,
  PlaceholderKey.CHAMPIONSHIP_NAME,
  PlaceholderKey.TOURNAMENT_NAME,
  PlaceholderKey.TOURNAMENT_LOGO,
  PlaceholderKey.MATCH_DATE,
  PlaceholderKey.MATCH_TIME,
  PlaceholderKey.MATCH_VENUE,
  PlaceholderKey.ROUND_NAME,
];

const SCHEDULE_KEYS = set(...SCHEDULE_BASE_KEYS);
const RESULTS_KEYS = set(...SCHEDULE_BASE_KEYS, PlaceholderKey.SCORE, PlaceholderKey.SCORE_TEAM);

const TOURNAMENT_TABLE_KEYS = set(
  PlaceholderKey.TEAM_NAME,
  PlaceholderKey.TEAM_SHORT_NAME,
  PlaceholderKey.TEAM_LOGO,
  PlaceholderKey.CHAMPIONSHIP_NAME,
  PlaceholderKey.TOURNAMENT_NAME,
  PlaceholderKey.TOURNAMENT_LOGO,
);

const TOP_PLAYERS_KEYS = set(
  PlaceholderKey.TEAM_NAME,
  PlaceholderKey.TEAM_SHORT_NAME,
  PlaceholderKey.TEAM_LOGO,
  PlaceholderKey.PLAYER_NAME,
  PlaceholderKey.PLAYER_NUMBER,
  PlaceholderKey.PLAYER_PHOTO,
  PlaceholderKey.CHAMPIONSHIP_NAME,
  PlaceholderKey.TOURNAMENT_NAME,
  PlaceholderKey.TOURNAMENT_LOGO,
  PlaceholderKey.ROUND_NAME,
);

const INDICATOR_SUBTYPE_OPTIONS = [
  { value: IndicatorSubtype.GOALS, label: 'Голы' },
  { value: IndicatorSubtype.ASSISTS, label: 'Голевые передачи' },
  { value: IndicatorSubtype.YELLOW_CARD, label: 'Жёлтые карточки' },
  { value: IndicatorSubtype.RED_CARD, label: 'Красные карточки' },
] as const;

export const TEMPLATE_TYPE_REGISTRY: Record<TemplateType, TemplateTypeEntry> = {
  [TemplateType.SCHEDULE]: { label: 'Расписание', allowedPlaceholderKeys: SCHEDULE_KEYS },
  [TemplateType.MULTIPLE_SCHEDULE]: { label: 'Несколько матчей (расписание)', allowedPlaceholderKeys: SCHEDULE_KEYS },
  [TemplateType.RESULTS]: { label: 'Результаты', allowedPlaceholderKeys: RESULTS_KEYS },
  [TemplateType.MULTIPLE_RESULTS]: { label: 'Несколько матчей (результаты)', allowedPlaceholderKeys: RESULTS_KEYS },
  [TemplateType.TOURNAMENT_TABLE]: { label: 'Турнирная таблица', allowedPlaceholderKeys: TOURNAMENT_TABLE_KEYS },
  [TemplateType.TOP_PLAYERS_BY_INDICATORS]: {
    label: 'Топ игроков по показателям',
    allowedPlaceholderKeys: TOP_PLAYERS_KEYS,
    subtypes: {
      label: 'Показатель',
      options: [...INDICATOR_SUBTYPE_OPTIONS],
      multi: false,
      required: true,
    },
  },
};

export function getTemplateTypeEntry(type: TemplateType): TemplateTypeEntry {
  return TEMPLATE_TYPE_REGISTRY[type];
}

export function isPlaceholderKeyValidFor(type: TemplateType, key: string | undefined | null): boolean {
  if (!key) return false;
  return (TEMPLATE_TYPE_REGISTRY[type].allowedPlaceholderKeys as ReadonlySet<string>).has(key);
}

export function availableTemplateTypes(): readonly { value: TemplateType; label: string }[] {
  return (Object.keys(TEMPLATE_TYPE_REGISTRY) as TemplateType[]).map((t) => ({
    value: t,
    label: TEMPLATE_TYPE_REGISTRY[t].label,
  }));
}
